import { useState, useCallback } from 'react';
import { Helmet } from 'react-helmet';

import { InputWithConfirm, RadioOptionsWithConfirm, CardContainer, CardWithModal } from 'shared-components';
import QuestionPage from '../../../templates/question';
import { ConfigField, IOtherName } from '../../../../types';
import { Q } from '../../../../store/student/types';
import { titleAppend } from '../../../../utilities/head';
import OtherNames from './otherNames';
import { useDispatch, useSelector } from 'react-redux';
import * as StudentActions from '../../../../store/student/actions';
import * as PIActions from '../../../../store/student/personalInfo/actions';
import { createNavigateForwardAction, createNavigateToAction } from '../../../../middleware/actions';
import { Pages } from '../../../../store/student/personalInfo/types';
import { getPageStatus } from './utils';
import { ApplicationState } from '../../../../store';

const useData = () => {
  const config = useSelector((state: ApplicationState) => state.config);
  const student = useSelector((state: ApplicationState) => state.student);
  const sectionTitle = useSelector((state: ApplicationState) => state.config.Checklist.PersonalInfo.shortTitle);

  const {
    GivenName: givenNameConfig,
    MiddleName: middleNameConfig,
    FamilyName: surnameConfig,
    HasOtherNames: hasOtherNamesConfig
  }: {
    [key: string]: ConfigField;
  } = useSelector((state: ApplicationState) => state.config.Checklist.PersonalInfo.Page1);

  const {
    surname = surnameConfig?.defaultValue,
    middleName = middleNameConfig?.defaultValue,
    givenName = givenNameConfig?.defaultValue,
    hasOtherNames = hasOtherNamesConfig?.defaultValue,
    otherNames = []
  } = useSelector((state: ApplicationState) => state.student.personalInfo);

  const { complete, visible } = getPageStatus(
    config,
    {
      surname,
      middleName,
      givenName,
      hasOtherNames,
      otherNames
    },
    student.importData
  );

  const {
    surname: surnameVisible,
    middleName: middleNameVisible,
    givenName: givenNameVisible,
    hasOtherNames: hasOtherNamesVisible
  } = visible;

  return {
    sectionTitle,
    complete,
    currentStep: 1,
    steps: 10,
    surname,
    surnameConfig,
    surnameVisible,
    middleName,
    middleNameVisible,
    middleNameConfig,
    givenName,
    givenNameConfig,
    givenNameVisible,
    hasOtherNames,
    hasOtherNamesConfig,
    hasOtherNamesVisible,
    otherNames,
    config,
    isPageComplete: student.offer.isPIPage1Complete,
    importData: student.importData
  };
};

const PersonalQPage1 = () => {
  const {
    sectionTitle,
    complete,
    currentStep,
    steps,
    surname,
    surnameConfig,
    surnameVisible,
    middleName,
    middleNameVisible,
    middleNameConfig,
    givenName,
    givenNameConfig,
    givenNameVisible,
    hasOtherNames,
    hasOtherNamesConfig,
    hasOtherNamesVisible,
    otherNames,
    config,
    isPageComplete,
    importData
  } = useData();

  const getImportApprovedFields = useCallback((): Array<Q> => {
    const importApproved = [];
    if (isPageComplete) {
      if (importData?.givenName === givenName) {
        importApproved.push(Q.GIVEN_NAME);
      }
      if (importData?.otherName === middleName) {
        importApproved.push(Q.MIDDLE_NAME);
      }
      if (importData?.familyName === surname) {
        importApproved.push(Q.SURNAME);
      }
    }
    return importApproved;
  }, [isPageComplete, importData, givenName, middleName, surname]);

  const getImportUpdateFields = useCallback((): Array<Q> => {
    const importUpdateEnabled = [];
    if (isPageComplete) {
      if (importData.givenName && givenName !== importData.givenName) {
        importUpdateEnabled.push(Q.GIVEN_NAME);
      }
      if (importData.otherName && middleName !== importData.otherName) {
        importUpdateEnabled.push(Q.MIDDLE_NAME);
      }
      if (importData.familyName && surname !== importData.familyName) {
        importUpdateEnabled.push(Q.SURNAME);
      }
    }
    return importUpdateEnabled;
  }, [isPageComplete, importData, givenName, middleName, surname]);

  const [importApproved, setImportApproved] = useState<Array<Q>>(getImportApprovedFields());
  const [importEdited, setImportEdited] = useState<Array<Q>>([]);
  const [showModal, setShowModal] = useState<Array<boolean>>([...otherNames.map(_ => false), false]);

  const dispatch = useDispatch();
  const onNext = config => {
    dispatch(StudentActions.savePersonalInfo(Pages.PAGE1, config));
    dispatch(createNavigateForwardAction());
  };
  const onSkip = () => dispatch(createNavigateForwardAction());
  const onChange = (fieldName: string, value, refValue?) =>
    dispatch(PIActions.createUpdatePIFieldAction(fieldName, value, refValue));
  const onBackToTaskList = () => {
    dispatch(createNavigateToAction('/dashboard'));
  };

  const toggleModal = useCallback((index: number) => {
    setShowModal(prevShowModal => {
      const newShowModal = [...prevShowModal];
      newShowModal[index] = !newShowModal[index];
      return newShowModal;
    });
  }, []);

  const handleYesNoClick = (target: Q, question) => {
    onChange(target, question.char === 'Y' ? true : false);
  };

  const deleteOtherName = (index: number): void => {
    const newOtherNames = [...otherNames];
    onChange(Q.OTHER_NAMES, [...newOtherNames.slice(0, index), ...newOtherNames.slice(index + 1)]);
  };

  const isImportEnabled = useCallback(
    (target: Q): boolean => {
      switch (target) {
        case Q.SURNAME:
          return surnameVisible && !!importData.familyName;
        case Q.MIDDLE_NAME:
          return middleNameVisible && !!importData.otherName;
        case Q.GIVEN_NAME:
          return givenNameVisible && !!importData.givenName;
        default:
          return false;
      }
    },
    [
      givenNameVisible,
      importData.familyName,
      importData.givenName,
      importData.otherName,
      middleNameVisible,
      surnameVisible
    ]
  );

  const isImportUpdateEnabled = useCallback(
    (target: Q): boolean => {
      if (!isPageComplete) return false;
      const importUpdateEnabled: Array<Q> = getImportUpdateFields();

      switch (target) {
        case Q.SURNAME:
          return !!surname && importUpdateEnabled.includes(target);
        case Q.MIDDLE_NAME:
          return !!middleName && importUpdateEnabled.includes(target);
        case Q.GIVEN_NAME:
          return !!givenName && importUpdateEnabled.includes(target);
        default:
          return false;
      }
    },
    [isPageComplete, getImportUpdateFields, surname, middleName, givenName]
  );

  const isImportComplete = (target: Q): boolean => {
    if (!isImportEnabled(target) || isImportUpdateEnabled(target)) return true;
    return importApproved.includes(target) || importEdited.includes(target);
  };

  const getComplete = (): boolean => {
    const importQuestions = [Q.SURNAME, Q.GIVEN_NAME];
    const importComplete = importQuestions.every(q => isImportComplete(q));
    return complete && importComplete;
  };

  const handleImportApprove = (target: Q): void => {
    setImportApproved(prevImportApproved => [...prevImportApproved, target]);
    setImportEdited(prevImportEdited => prevImportEdited.filter(q => q !== target));
  };

  const handleImportEdit = (target: Q): void => {
    setImportApproved(prevImportApproved => prevImportApproved.filter(q => q !== target));
    setImportEdited(prevImportEdited => [...prevImportEdited, target]);
  };

  const handleImportUndo = (target: Q): void => {
    setImportApproved(prevImportApproved => prevImportApproved.filter(q => q !== target));
    setImportEdited(prevImportEdited => prevImportEdited.filter(q => q !== target));
  };

  return (
    <QuestionPage
      title={sectionTitle}
      subtitle="All names must match your passport exactly."
      complete={getComplete()}
      currentStep={currentStep}
      steps={steps}
      onNext={() => onNext(config)}
      onSkip={onSkip}
      onBackToTaskList={onBackToTaskList}
    >
      <Helmet>
        <title>
          {`${sectionTitle}: Step ${currentStep}`} {titleAppend}
        </title>
      </Helmet>

      {givenNameVisible && (
        <InputWithConfirm
          key={Q.GIVEN_NAME}
          id={Q.GIVEN_NAME}
          value={givenName}
          label={`${givenNameConfig?.label}${!middleNameVisible ? 's' : ''}`}
          subText={givenNameConfig?.subLabel}
          inputHelperText={givenNameConfig?.helpText}
          onChange={evt => onChange(Q.GIVEN_NAME, evt.currentTarget.value)}
          autoFocus={true}
          importEnabled={isImportEnabled(Q.GIVEN_NAME)}
          importValue={importData.givenName}
          importUpdateEnabled={isImportUpdateEnabled(Q.GIVEN_NAME)}
          importApproved={importApproved.includes(Q.GIVEN_NAME)}
          importEdited={importEdited.includes(Q.GIVEN_NAME)}
          onImportApprove={() => {
            handleImportApprove(Q.GIVEN_NAME);
            onChange(Q.GIVEN_NAME, importData.givenName);
          }}
          onImportEdit={() => {
            handleImportEdit(Q.GIVEN_NAME);
            onChange(Q.GIVEN_NAME, isImportUpdateEnabled(Q.GIVEN_NAME) ? givenName : importData.givenName);
          }}
          onImportUndo={() => {
            handleImportUndo(Q.GIVEN_NAME);
            onChange(Q.GIVEN_NAME, isImportUpdateEnabled(Q.GIVEN_NAME) ? givenName : '');
          }}
        />
      )}

      {middleNameVisible && (
        <InputWithConfirm
          key={Q.MIDDLE_NAME}
          id={Q.MIDDLE_NAME}
          value={middleName}
          label={middleNameConfig?.label}
          subText={middleNameConfig?.subLabel}
          onChange={evt => onChange(Q.MIDDLE_NAME, evt.currentTarget.value)}
          importEnabled={isImportEnabled(Q.MIDDLE_NAME)}
          importValue={importData.otherName}
          importUpdateEnabled={isImportUpdateEnabled(Q.MIDDLE_NAME)}
          importApproved={importApproved.includes(Q.MIDDLE_NAME)}
          importEdited={importEdited.includes(Q.MIDDLE_NAME)}
          onImportApprove={() => {
            handleImportApprove(Q.MIDDLE_NAME);
            onChange(Q.MIDDLE_NAME, importData.otherName);
          }}
          onImportEdit={() => {
            handleImportEdit(Q.MIDDLE_NAME);
            onChange(Q.MIDDLE_NAME, isImportUpdateEnabled(Q.MIDDLE_NAME) ? middleName : importData.otherName);
          }}
          onImportUndo={() => {
            handleImportUndo(Q.MIDDLE_NAME);
            onChange(Q.MIDDLE_NAME, isImportUpdateEnabled(Q.MIDDLE_NAME) ? middleName : '');
          }}
        />
      )}

      {surnameVisible && (
        <InputWithConfirm
          key={Q.SURNAME}
          id={Q.SURNAME}
          value={surname}
          label={surnameConfig?.label}
          subText={surnameConfig?.subLabel}
          inputHelperText={surnameConfig?.helpText}
          onChange={evt => onChange(Q.SURNAME, evt.currentTarget.value)}
          importEnabled={isImportEnabled(Q.SURNAME)}
          importValue={importData.familyName}
          importUpdateEnabled={isImportUpdateEnabled(Q.SURNAME)}
          importApproved={importApproved.includes(Q.SURNAME)}
          importEdited={importEdited.includes(Q.SURNAME)}
          onImportApprove={() => {
            handleImportApprove(Q.SURNAME);
            onChange(Q.SURNAME, importData?.familyName);
          }}
          onImportEdit={() => {
            handleImportEdit(Q.SURNAME);
            onChange(Q.SURNAME, isImportUpdateEnabled(Q.SURNAME) ? surname : importData?.familyName);
          }}
          onImportUndo={() => {
            handleImportUndo(Q.SURNAME);
            onChange(Q.SURNAME, isImportUpdateEnabled(Q.SURNAME) ? surname : '');
          }}
        />
      )}

      {hasOtherNamesVisible && (
        <RadioOptionsWithConfirm
          key={Q.HAS_OTHER_NAMES}
          id={Q.HAS_OTHER_NAMES}
          value={hasOtherNames}
          label={hasOtherNamesConfig?.label}
          inputHelperText={hasOtherNamesConfig?.helpText}
          onClick={question => handleYesNoClick(Q.HAS_OTHER_NAMES, question)}
          isInline={true}
          questions={[
            {
              char: 'Y',
              label: 'Yes',
              selected: hasOtherNames === true
            },
            {
              char: 'N',
              label: 'No',
              selected: hasOtherNames === false
            }
          ]}
        />
      )}

      {hasOtherNames === true && (
        <CardContainer title={`Alternative or Previous Names`}>
          {[...otherNames, { surname: '', givenName: '', reason: '' }].map((otherName: IOtherName, index: number) => (
            <CardWithModal
              key={index}
              id={`other-name-${index}`}
              index={index}
              modalTitle={'Add an Alternative or Previous Name'}
              modalBlurb={`Please enter this exactly as it appeared on previous passports/ID cards`}
              cardItems={[
                {
                  label: 'Given Names',
                  value: otherName.givenName
                },
                {
                  label: 'Family Name',
                  value: otherName.surname
                },
                {
                  label: 'Name Reason',
                  value: otherName.reason
                }
              ]}
              onRemove={deleteOtherName}
              addButtonLabel={`Add Alternative or Previous Name`}
              addAnotherButtonLabel={`I have another name to add`}
              hasAddMoreButton={otherName.givenName === '' && otherName.surname === '' && otherName.reason === ''}
              showModal={showModal[index]}
              toggleModal={toggleModal}
            >
              <OtherNames
                index={index}
                givenName={otherName.givenName}
                givenNameSubText={givenNameConfig?.subLabel}
                surname={otherName.surname}
                surnameSubText={surnameConfig?.subLabel}
                surnameHelpText={surnameConfig?.helpText as string}
                reason={otherName.reason}
                onSaveAndClose={(newOtherName: IOtherName) => {
                  const updatedOtherNames = [...otherNames];
                  updatedOtherNames[index] = { ...newOtherName };
                  onChange(Q.OTHER_NAMES, updatedOtherNames);
                  toggleModal(index);
                }}
              />
            </CardWithModal>
          ))}
        </CardContainer>
      )}
    </QuestionPage>
  );
};

export default PersonalQPage1;
